import * as React from 'react'
import { CardElement, Elements, useStripe, useElements } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import styled from 'styled-components'
import I18n from '../../../core/i18n'
import { settingsService, utilService } from '../../../core/services'
import * as constants from '../../../static/constants'
import { getStripePublicKey } from '../../../utils'
import { Button, Panel } from '../../atoms'
import { FlashMessage } from '../../organisms'
import SettingsLayout from '../_layouts/settingsLayout'

const stripePromise = loadStripe(getStripePublicKey())

interface IWindow {
  flashMessages: FlashMessage
}
declare var window: IWindow

interface ICard {
  brand: string
  exp_month: number
  exp_year: number
  id: number
  last4: string
}

interface IProps {
  user: any
}

const createOptions = () => {
  return {
    hidePostalCode: true,
    style: {
      base: {
        fontSize: '1em',
        color: constants.COLORS.black,
        letterSpacing: '0.5px',
        fontFamily: 'Source Code Pro, monospace',
        '::placeholder': {
          color: '#aab7c4',
        },
      },
      invalid: {
        color: constants.COLORS.caution,
      },
    },
  }
}

interface IStripeFormProps {
  isNew: boolean
  setCard(card: ICard): void
  setEdit(edit: boolean): void
  setIsNew?(isNew: boolean): void
}

const StripeForm: React.FC<IStripeFormProps> = props => {
  const stripe = useStripe()
  const elements = useElements()
  const [isProcessing, setIsProcessing] = React.useState(false)

  const handleSubmit = async event => {
    event.preventDefault()
    setIsProcessing(true)

    const cardElement = elements.getElement(CardElement)
    const { secret } = await settingsService.createSecret()
    let paymentMethod = null
    try {
      const result = await stripe.confirmCardSetup(secret, {
        payment_method: {
          card: cardElement,
        },
      })
      if (result.error) {
        window.flashMessages.addMessage({ text: result.error.message, type: 'error' })
        console.error(result.error)
        setIsProcessing(false)
        return
      }
      paymentMethod = result.setupIntent?.payment_method
    } catch (e) {
      console.error(e)
      setIsProcessing(false)
      return
    }

    if (paymentMethod) {
      const { card, flush } = await settingsService.createCard(paymentMethod)
      if (card) {
        props.setCard(card)
        props.setEdit(false)
        if (props.isNew) {
          props.setIsNew(false)
        }
      }
      setIsProcessing(false)
      window.flashMessages.addMessage({ text: flush.message, type: flush.type })
    } else {
      setIsProcessing(false)
    }
  }

  const renderButtons = () => {
    return props.isNew ? (
      <Button primary={true}>{I18n.t('generic.registration')}</Button>
    ) : (
      <>
        <Button primary={true}>{I18n.t('generic.change')}</Button>
        <Button handleClick={() => props.setEdit(false)}>{I18n.t('generic.cancel')}</Button>
      </>
    )
  }

  return (
    <form onSubmit={handleSubmit}>
      <div>
        <CardElement options={createOptions()} />
        {isProcessing ? <div>{I18n.t('generic.connecting')}</div> : renderButtons()}
      </div>
    </form>
  )
}

interface IProps {
  card?: ICard
  isNew: boolean
}

const CardShow: React.FC<IProps> = props => {
  const [card, setCard] = React.useState<ICard>({ ...props.card })
  const [edit, setEdit] = React.useState(false)
  const [isNew, setIsNew] = React.useState(props.isNew)
  const { data: user } = utilService.getDataFromJson(props.user)

  const renderRegisterCard = () => {
    return (
      <>
        <h3>{I18n.t('card.register_card')}</h3>
        <Elements stripe={stripePromise}>
          <StripeForm isNew={isNew} setCard={setCard} setEdit={setEdit} setIsNew={setIsNew} />
        </Elements>
      </>
    )
  }

  const renderEditCard = () => {
    return (
      <>
        <h3>{I18n.t('card.registered_cards')}</h3>
        <div>
          {edit ? (
            <Elements stripe={stripePromise}>
              <StripeForm setCard={setCard} setEdit={setEdit} isNew={false} />
            </Elements>
          ) : (
            <div>
              <p className="CardShow_RegisteredCard">
                <span>{card.brand}</span>
                <span>{I18n.t('card.card_end')}: </span>
                <span>{card.last4}</span>
                <span className="exp">{I18n.t('generic.expiration_date')}: </span>
                <span>
                  {card.exp_month}/{card.exp_year}
                </span>
              </p>
              <Button handleClick={() => setEdit(true)}>{I18n.t('generic.change')}</Button>
            </div>
          )}
        </div>
      </>
    )
  }
  return (
    <SettingsLayout
      active_path="card"
      user={props.user}
      backgroundColor={user.guest ? 'white' : 'default'}
    >
      <Panel title={I18n.t('generic.card')} noDecoration={true}>
        <CardShowWrapper>{isNew ? renderRegisterCard() : renderEditCard()}</CardShowWrapper>
      </Panel>
    </SettingsLayout>
  )
}

const CardShowWrapper = styled.div`

  h3 {
    font-size: 16px;
  }

  .CardShow_RegisteredCard {
    margin-top: 24px;

    span + span {
      margin-left: 8px;
    }

    span.exp {
      margin-left: 24px;
    }

    & + .Button {
      margin-top: 16px;
    }
  }

  .StripeElement {
    display: block;
    margin: 10px 0 20px 0;
    max-width: 500px;
    padding: 10px 14px;
    box-shadow: ${constants.COLORS.cardShadow}
    border-radius: 4px;
    background: white;
    border-bottom: 1px solid ${constants.COLORS.divider};
  }

  .StripeElement--focus {
    box-shadow: ${constants.COLORS.cardShadow}
    transition: all 150ms ease;
    border-bottom: 1px solid ${constants.COLORS.secondary}

  }

  .StripeElement.PaymentRequestButton {
    padding: 0;
  }
  }

`

export default CardShow
